/* eslint-disable max-lines */
import { useMemo, useState } from 'react';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap';
import { toast } from 'react-toastify';
import TaskBoardService from '../../../services/TaskBoard';

type BoardList = { id: string; name: string };
type BoardMember = { userId: string; user?: { firstName?: string; lastName?: string } | null };

type Props = {
  isOpen: boolean;
  onClose: () => void;
  token: string;
  schoolId: string;
  lists: BoardList[];
  members: BoardMember[];
  onRefresh: () => Promise<void>;
};

type RowObject = Record<string, string>;

const fieldOptions = [
  { key: 'title', label: 'title' },
  { key: 'description', label: 'description' },
  { key: 'due_date', label: 'due_date' },
  { key: 'list_name', label: 'list_name' },
  { key: 'labels', label: 'labels' },
  { key: 'assigned_to', label: 'assigned_to' },
];

const normalizeHeader = (text: string) => text.trim().toLowerCase().replace(/\s+/g, '_');

const normalizeDueDate = (raw: unknown): string | null => {
  if (!raw) return null;
  if (raw instanceof Date) {
    const m = moment(raw);
    return m.isValid() ? m.format('YYYY-MM-DD') : null;
  }
  const str = String(raw).trim();
  if (!str) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const mdyMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdyMatch) {
    const m = moment(str, 'MM/DD/YYYY');
    return m.isValid() ? m.format('YYYY-MM-DD') : null;
  }
  const m = moment(str);
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
};

const TaskBoardImportModal = ({ isOpen, onClose, token, schoolId, lists, members, onRefresh }: Props) => {
  const [sourceFileName, setSourceFileName] = useState('');
  const [rows, setRows] = useState<RowObject[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState<{ created: number; errors: string[] } | null>(null);

  const parseFile = async (file: File) => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array', cellDates: true });
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(firstSheet, { defval: '' });
    const normalized = json.map((row) =>
      Object.keys(row).reduce<RowObject>((acc, key) => {
        const normalizedKey = normalizeHeader(key);
        const rawValue = row[key];
        if (normalizedKey === 'due_date') {
          acc[normalizedKey] = normalizeDueDate(rawValue) ?? '';
        } else {
          acc[normalizedKey] = String(rawValue ?? '').trim();
        }
        return acc;
      }, {}),
    );
    const discovered = normalized.length ? Object.keys(normalized[0]) : [];
    setRows(normalized);
    setHeaders(discovered);
    setSourceFileName(file.name);
    setReport(null);

    const defaultMapping: Record<string, string> = {};
    for (const option of fieldOptions) {
      const matched = discovered.find((header) => header === option.key);
      if (matched) defaultMapping[option.key] = matched;
    }
    setMapping(defaultMapping);
  };

  const mappedRows = useMemo(() => {
    return rows.map((row) => {
      const pick = (key: string) => row[mapping[key] || ''] || '';
      return {
        title: pick('title'),
        description: pick('description'),
        dueDate: pick('due_date'),
        listName: pick('list_name'),
        labels: pick('labels'),
        assignedTo: pick('assigned_to'),
      };
    });
  }, [rows, mapping]);

  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    if (!mapping.title) errors.push('Required mapping missing: title.');
    if (!mapping.list_name) errors.push('Required mapping missing: list_name.');

    mappedRows.forEach((row, idx) => {
      const line = idx + 2;
      if (!row.title.trim()) errors.push(`Row ${line}: title is required.`);
      if (!row.listName.trim()) errors.push(`Row ${line}: list_name is required.`);
      if (row.listName && !lists.some((list) => list.name.toLowerCase() === row.listName.toLowerCase())) {
        errors.push(`Row ${line}: list_name "${row.listName}" does not exist.`);
      }
      if (row.dueDate && !moment(row.dueDate, 'YYYY-MM-DD', true).isValid()) {
        errors.push(`Row ${line}: invalid due_date "${row.dueDate}" — use MM/DD/YYYY (e.g. 04/10/2026).`);
      }
    });
    return errors;
  }, [mapping, mappedRows, lists]);

  const downloadTemplate = () => {
    const templateRows = [
      {
        title: 'Prepare family newsletter',
        description: 'Draft and review content for this month.',
        due_date: '04/10/2026',
        list_name: lists[0]?.name || 'Center Todo',
        labels: 'Request, Monthly Curriculum Event',
        assigned_to: 'Jane Doe, John Smith',
      },
    ];
    const ws = XLSX.utils.json_to_sheet(templateRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'tasks');
    XLSX.writeFile(wb, 'task-board-template.xlsx');
  };

  const doImport = async () => {
    if (!rows.length) return;
    if (validationErrors.length) {
      toast.error('Fix validation errors before importing.');
      return;
    }
    setImporting(true);
    const service = TaskBoardService(token);
    let created = 0;
    const errors: string[] = [];
    const listByName = new Map(lists.map((list) => [list.name.toLowerCase(), list.id]));
    const memberNameToId = new Map(
      members.map((member) => [
        `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim().toLowerCase(),
        member.userId,
      ]),
    );

    for (let i = 0; i < mappedRows.length; i++) {
      const row = mappedRows[i];
      const line = i + 2;
      try {
        const listId = listByName.get(row.listName.trim().toLowerCase());
        if (!listId) {
          errors.push(`Row ${line}: invalid list_name value.`);
          continue;
        }
        const createResponse = await service.createCard(schoolId, { listId, title: row.title.trim() });
        const cardId = createResponse?.data?.id || createResponse?.id;
        if (!cardId) {
          errors.push(`Row ${line}: failed to create card.`);
          continue;
        }

        const simpleLabels = row.labels
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);

        const memberUserIds = row.assignedTo
          .split(',')
          .map((item) => item.trim().toLowerCase())
          .filter(Boolean)
          .map((name) => memberNameToId.get(name))
          .filter(Boolean) as string[];

        await service.updateCard(schoolId, cardId, {
          description: row.description || null,
          dueDate: row.dueDate || null,
          simpleLabels,
          memberUserIds,
          checklists: [],
        });
        created += 1;
      } catch (e: any) {
        errors.push(`Row ${line}: ${String(e?.response?.data?.message || 'import error')}.`);
      }
    }

    setReport({ created, errors });
    await onRefresh();
    setImporting(false);
  };

  const preview = mappedRows.slice(0, 8);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl">
      <ModalHeader toggle={onClose}>Import Tasks</ModalHeader>
      <ModalBody>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <Label className="mb-1">File (.csv, .xlsx)</Label>
            <Input
              type="file"
              accept=".csv,.xlsx"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void parseFile(file);
              }}
            />
            {!!sourceFileName && <small className="text-muted">File: {sourceFileName}</small>}
          </div>
          <Button color="secondary" outline onClick={downloadTemplate}>
            Download template
          </Button>
        </div>

        {!!headers.length && (
          <div className="mb-3">
            <Label className="d-block">Column mapping</Label>
            <div className="row">
              {fieldOptions.map((field) => (
                <div className="col-md-4 mb-2" key={field.key}>
                  <small className="text-muted d-block mb-1">{field.label}</small>
                  <Input
                    type="select"
                    value={mapping[field.key] || ''}
                    onChange={(e) => setMapping((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  >
                    <option value="">-- not mapped --</option>
                    {headers.map((header) => (
                      <option value={header} key={`${field.key}-${header}`}>
                        {header}
                      </option>
                    ))}
                  </Input>
                </div>
              ))}
            </div>
          </div>
        )}

        {!!preview.length && (
          <>
            <Label className="d-block">Preview</Label>
            <div style={{ maxHeight: 240, overflow: 'auto' }}>
              <Table bordered size="sm">
                <thead>
                  <tr>
                    <th>title</th>
                    <th>description</th>
                    <th>due_date</th>
                    <th>list_name</th>
                    <th>labels</th>
                    <th>assigned_to</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row, idx) => (
                    <tr key={`preview-${idx}`}>
                      <td>{row.title}</td>
                      <td>{row.description}</td>
                      <td>{row.dueDate ? moment(row.dueDate, 'YYYY-MM-DD').format('MM/DD/YYYY') : ''}</td>
                      <td>{row.listName}</td>
                      <td>{row.labels}</td>
                      <td>{row.assignedTo}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}

        {!!validationErrors.length && (
          <div className="mt-3">
            <Label className="d-block text-danger">Validation</Label>
            <ul className="mb-0">
              {validationErrors.slice(0, 20).map((error, idx) => (
                <li key={`err-${idx}`} className="text-danger">
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {report && (
          <div className="mt-3">
            <Label className="d-block">Final report</Label>
            <div>
              {report.created} cards created successfully, {report.errors.length} errors.
            </div>
            {!!report.errors.length && (
              <ul className="mb-0 mt-2">
                {report.errors.slice(0, 30).map((error, idx) => (
                  <li key={`report-err-${idx}`}>{error}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose} disabled={importing}>
          Close
        </Button>
        <Button color="primary" onClick={() => void doImport()} disabled={importing || !rows.length}>
          {importing ? 'Importing...' : 'Confirm Import'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TaskBoardImportModal;
